import { createContext, useState, useRef, useEffect, useCallback } from "react";

import inboxService from "../services/inbox.service";
import MessagesService from "../services/messages.service";
import UIkit from "uikit";

import { getAirBnBThreads } from "../services/airbnb.service.js";
import {
  applyRealtimeInboxMessage,
  extractThreadMetaTokens,
  hasThreadMeta,
  mergeInboxPage,
  promoteInboxThread as promoteInboxThreadInList,
  threadMatchesFilter,
  updateThreadMetaInList,
  setThreadMetaValue,
} from "./inbox-state";
import {
  attachAirbnbThreadsToSources,
  appendMessageToCurrentThread,
  confirmPendingMessageInCurrentThread,
  getAirbnbThreadIds,
  isAirbnbSource,
  mergeThreadDetailSnapshot,
  removeMessageFromCurrentThread,
  sameThreadId,
  updateThreadDetailIfCurrent,
} from "./thread-detail-state";

const defaultRecipient = {
  first_name: "",
  last_name: "",
  email: "",
  schedule: [
    {
      weekday: 2,
      start_time: "2001-01-02 09:00:00",
      end_time: "2001-01-02 17:00:00",
    },
    {
      weekday: 3,
      start_time: "2001-01-03 09:00:00",
      end_time: "2001-01-03 17:00:00",
    },
    {
      weekday: 4,
      start_time: "2001-01-04 09:00:00",
      end_time: "2001-01-04 17:00:00",
    },
    {
      weekday: 5,
      start_time: "2001-01-05 09:00:00",
      end_time: "2001-01-05 17:00:00",
    },
    {
      weekday: 6,
      start_time: "2001-01-06 09:00:00",
      end_time: "2001-01-06 17:00:00",
    },
  ],
};

export const InboxContext = createContext();
export const InboxProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isSendingFlag, setIsSending] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [settings, setSettings] = useState(null);
  const [inbox, setInboxState] = useState(null);
  const [threadCnt, setThreadCnt] = useState(0);
  const [pendingMeta, setPendingMeta] = useState({});
  const [reservation, setReservation] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedThreadId, setSelectedThreadIdState] = useState(0);
  const [threadDetail, setThreadDetailState] = useState({});
  // const [currentIndexOnPage, setCurrentIndexOnPage] = useState(-1);
  const [, setMessageOffset] = useState(0);
  const messageOffsetRef = useRef(0);
  const inboxRef = useRef(null);
  const activeQueryRef = useRef({ key: null, filter: "", requests: {} });
  const requestSequenceRef = useRef(0);
  const mutationSequenceRef = useRef(0);
  const threadMutationRef = useRef({});
  const pendingMetaRef = useRef({});
  const selectedThreadIdRef = useRef(0);
  const detailRequestRef = useRef(0);

  const [recipient, setRecipient] = useState(
    JSON.parse(JSON.stringify(defaultRecipient)),
  );
  const [deletedRecipient, setDeletedRecipient] = useState(null);
  const [recipientAction, setRecipientAction] = useState(null);
  const [recipientPending, setRecipientPending] = useState(false);
  const [invitePending, setInvitePending] = useState(false);

  const [legacy, setLegacy] = useState(false);

  const setInbox = (nextInbox) => {
    setInboxState((previous) => {
      const next =
        typeof nextInbox === "function" ? nextInbox(previous) : nextInbox;
      inboxRef.current = next;
      return next;
    });
  };

  const setSelectedThreadId = (nextThreadId) => {
    const next =
      typeof nextThreadId === "function"
        ? nextThreadId(selectedThreadIdRef.current)
        : nextThreadId;
    if (!sameThreadId(selectedThreadIdRef.current, next)) {
      detailRequestRef.current += 1;
    }
    selectedThreadIdRef.current = next;
    setSelectedThreadIdState(next);
  };

  const setThreadDetail = (nextThreadDetail) => {
    setThreadDetailState((previous) =>
      typeof nextThreadDetail === "function"
        ? nextThreadDetail(previous)
        : nextThreadDetail,
    );
  };

  const updateCurrentThreadDetail = useCallback((threadId, update) => {
    setThreadDetailState((previous) =>
      updateThreadDetailIfCurrent(
        previous,
        selectedThreadIdRef.current,
        threadId,
        update,
      ),
    );
  }, []);

  const beginThreadSelection = (threadId) => {
    const requestId = ++detailRequestRef.current;
    const selectionChanged = !sameThreadId(
      selectedThreadIdRef.current,
      threadId,
    );
    selectedThreadIdRef.current = threadId;
    setSelectedThreadIdState(threadId);
    if (selectionChanged) setThreadDetailState({});
    return requestId;
  };

  const isCurrentThreadRequest = (threadId, requestId) =>
    detailRequestRef.current === requestId &&
    sameThreadId(selectedThreadIdRef.current, threadId);

  const beginInboxRequest = (scope, pageNo, filter, pageSize) => {
    const key = `${scope}:${filter || ""}:${pageSize || 20}`;
    const requestId = ++requestSequenceRef.current;
    const queryChanged = activeQueryRef.current.key !== key;

    if (queryChanged) {
      activeQueryRef.current = { key, filter: filter || "", requests: {} };
      const activeThread = (
        Array.isArray(inboxRef.current) ? inboxRef.current : []
      ).find((thread) =>
        sameThreadId(thread.id, selectedThreadIdRef.current),
      );
      const preserved =
        activeThread && threadMatchesFilter(activeThread, filter)
          ? [activeThread]
          : [];
      inboxRef.current = preserved;
      setInboxState(preserved);
      setThreadCnt(0);
    }
    activeQueryRef.current.requests[pageNo] = requestId;

    return {
      key,
      requestId,
      queryChanged,
      mutationRevision: mutationSequenceRef.current,
    };
  };

  const commitInboxPage = ({
    key,
    requestId,
    queryChanged,
    mutationRevision,
    pageNo,
    page,
    count,
    background,
  }) => {
    if (
      activeQueryRef.current.key !== key ||
      activeQueryRef.current.requests[pageNo] !== requestId
    ) {
      return false;
    }

    const currentById = new Map(
      (Array.isArray(inboxRef.current) ? inboxRef.current : []).map((thread) => [
        String(thread.id),
        thread,
      ]),
    );
    const safePage = (page || []).reduce((result, thread) => {
      if ((threadMutationRef.current[thread.id] || 0) > mutationRevision) {
        const current = currentById.get(String(thread.id));
        if (current) result.push(current);
      } else {
        result.push(thread);
      }
      return result;
    }, []);

    setInbox((previous) =>
      mergeInboxPage(previous, safePage, {
        replace: queryChanged || (pageNo === 0 && !background),
        position: pageNo === 0 ? "front" : "append",
        preserveIds: [selectedThreadIdRef.current],
        filter: activeQueryRef.current.filter,
      }),
    );
    if (mutationSequenceRef.current === mutationRevision) {
      setThreadCnt(Number(count) || 0);
    }
    return true;
  };

  const applyMetaState = useCallback((
    threadId,
    token,
    enabled,
    preserveMembership = false,
  ) => {
    const result = updateThreadMetaInList(
      inboxRef.current,
      threadId,
      token,
      enabled,
      activeQueryRef.current.filter,
      { preserveMembership },
    );
    inboxRef.current = result.threads;
    setInboxState(result.threads);
    if (result.removed) {
      setThreadCnt((count) => Math.max(0, Number(count) - 1));
    }
  }, []);

  useEffect(() => {
    let active = true;
    const requests = new Map();
    const receivePriorityChange = async ({ detail }) => {
      if (detail?.token !== "priority" || !detail.thread_id || !detail.personal_inbox_id) return;
      const key = String(detail.thread_id);
      const requestId = (requests.get(key) || 0) + 1;
      requests.set(key, requestId);
      try {
        const { data } = await inboxService.getPersonalThreadMeta(detail.thread_id, detail.personal_inbox_id);
        if (!active || requests.get(key) !== requestId || data.error) return;
        const enabled = (data.tokens || []).includes("priority");
        threadMutationRef.current[detail.thread_id] = ++mutationSequenceRef.current;
        applyMetaState(detail.thread_id, "priority", enabled);
        updateCurrentThreadDetail(detail.thread_id, (thread) => setThreadMetaValue(thread, "priority", enabled));
        // Reload folder membership, including newly flagged uncached threads.
        window.dispatchEvent(new Event("inbox-reconcile"));
      } catch (error) {
        window.dispatchEvent(new Event("inbox-reconcile"));
      }
    };
    window.addEventListener("thread-meta-changed", receivePriorityChange);
    return () => {
      active = false;
      window.removeEventListener("thread-meta-changed", receivePriorityChange);
    };
  }, [applyMetaState, updateCurrentThreadDetail]);

  const confirmThreadRead = (threadId) => {
    threadMutationRef.current[threadId] = ++mutationSequenceRef.current;
    applyMetaState(threadId, "unread", false);
  };

  const mutateMeta = async (threadId, token, requestedState, request) => {
    const key = `${threadId}:${token}`;
    if (pendingMetaRef.current[key]) return null;

    const currentThread = (
      Array.isArray(inboxRef.current) ? inboxRef.current : []
    ).find(
      (thread) => String(thread.id) === String(threadId),
    );
    const previousState = hasThreadMeta(currentThread, token);
    const nextState =
      typeof requestedState === "boolean" ? requestedState : !previousState;

    pendingMetaRef.current = { ...pendingMetaRef.current, [key]: true };
    setPendingMeta(pendingMetaRef.current);
    threadMutationRef.current[threadId] = ++mutationSequenceRef.current;
    applyMetaState(threadId, token, nextState, true);

    try {
      const response = await request();
      const data = response.data;
      const responseTokens = extractThreadMetaTokens(data?.thread);
      const hasServerMeta = data?.thread &&
        (data.thread.meta !== undefined || data.thread.meta_values !== undefined);
      const confirmedState = hasServerMeta ? responseTokens.includes(token) : nextState;
      applyMetaState(threadId, token, confirmedState, false);
      return data;
    } catch (error) {
      applyMetaState(threadId, token, previousState, true);
      throw error;
    } finally {
      threadMutationRef.current[threadId] = ++mutationSequenceRef.current;
      const { [key]: ignored, ...remaining } = pendingMetaRef.current;
      pendingMetaRef.current = remaining;
      setPendingMeta(remaining);
    }
  };

  let contextInbox = {
    loading,
    isSendingFlag,
    isNew,
    setIsNew,
    inbox,
    settings,
    setInbox,
    reservation,
    setReservation,
    filters,
    setFilters,
    selectedThreadId,
    setSelectedThreadId,
    setThreadDetail,
    threadDetail,
    threadCnt,
    setThreadCnt,
    pendingMeta,
    isMetaPending: (threadId, token) =>
      !!pendingMeta[`${threadId}:${token}`],

    recipient,
    setRecipient,
    deletedRecipient,
    setDeletedRecipient,
    recipientAction,
    setRecipientAction,
    recipientPending,
    setRecipientPending,
    invitePending,
    setInvitePending,

    legacy,
    setLegacy,

    // currentIndexOnPage,
    // setCurrentIndexOnPage,

    get: async (criteria = {}) => {
      setLoading(true);
      let resp = await inboxService.get(criteria);
      setSettings(resp.data);
      setLoading(false);
    },
    initRecipient: () => {
      setRecipient(defaultRecipient);
    },
    createRecipient: async (recipient) => {
      setRecipientPending(true);
      try {
        const resp = await inboxService.createRecipient(recipient);
        setRecipientPending(false);
        setSettings({ ...settings, recipients: resp.data });
        setRecipient(JSON.parse(JSON.stringify(defaultRecipient)));
        return resp.data;
      } catch (err) {
        console.warn(err);
        setRecipientPending(false);
        throw err;
      }
    },
    updateRecipient: async (recipient) => {
      setRecipientPending(true);
      try {
        const resp = await inboxService.updateRecipient(recipient);
        setSettings({ ...settings, recipients: resp.data });
        setRecipientPending(false);
        setRecipient(JSON.parse(JSON.stringify(defaultRecipient)));
        return resp.data;
      } catch (err) {
        console.warn(err);
        setRecipientPending(false);
        throw err;
      }
    },
    bulkUpdateSchedules: async (recipientIds, schedule) => {
      setRecipientPending(true);
      try {
        const resp = await inboxService.bulkUpdateSchedules(
          recipientIds,
          schedule,
        );
        setSettings({ ...settings, recipients: resp.data });
        setRecipientPending(false);
        return resp.data;
      } catch (err) {
        console.warn(err);
        setRecipientPending(false);
        throw err;
      }
    },
    deleteRecipient: async (recipient) => {
      setRecipientPending(true);
      inboxService
        .deleteRecipient(recipient.recipient_id)
        .then((resp) => {
          setSettings({ ...settings, recipients: resp.data });
          setRecipientPending(false);
          setRecipient(JSON.parse(JSON.stringify(defaultRecipient)));
        })
        .catch((err) => {
          console.warn(err);
          setRecipientPending(false);
          setRecipientAction(null);
        });
    },
    undeleteRecipient: async (recipient) => {
      setRecipientPending(true);
      inboxService
        .undeleteRecipient(recipient.recipient_id)
        .then((resp) => {
          setSettings({ ...settings, recipients: resp.data });
          setRecipientPending(false);
          setRecipient(JSON.parse(JSON.stringify(defaultRecipient)));
          setRecipientAction(null);
        })
        .catch((err) => {
          console.warn(err);
          setRecipientPending(false);
          setRecipientAction(null);
          UIkit.notification(
            "Sorry, this user could not be reinstated, please contact RueBaRue support.",
            "error",
          );
        });
    },
    inviteRecipient: async (recipient) => {
      setInvitePending(true);
      inboxService
        .inviteRecipient(recipient.recipient_id)
        .then(async (resp) => {
          await setSettings({ ...settings, recipients: resp.data });
          setInvitePending(false);
        })
        .catch((err) => {
          console.warn(err);
          setInvitePending(false);
        });
    },
    refresh: async (
      pageNo = 0,
      filter = "",
      pageSize = 20,
      stopLoading = false,
    ) => {
      const request = beginInboxRequest("legacy", pageNo, filter, pageSize);
      if (!stopLoading) {
        setLoading(true);
      }
      try {
        // TODO: update inbox page & filter
        const response = await inboxService.getInboxes(
          pageNo,
          filter,
          pageSize,
        );
        const { error, page, count } = response.data;

        if (error) {
          throw error;
        }

        commitInboxPage({
          ...request,
          pageNo,
          page,
          count,
          background: stopLoading,
        });
        return response.data;
      } catch (err) {
        console.warn(err);
        throw err;
      } finally {
        if (!stopLoading) setLoading(false);
      }
    },
    personalInbox: async (
      personalInboxId,
      pageNo = 0,
      filter = "",
      pageSize = 20,
      stopLoading = false,
    ) => {
      if (legacy) {
        return contextInbox.refresh(
          pageNo,
          filter,
          pageSize,
          stopLoading,
        );
      }

      const request = beginInboxRequest(
        `personal:${personalInboxId}`,
        pageNo,
        filter,
        pageSize,
      );
      if (!stopLoading) {
        setLoading(true);
      }
      try {
        // TODO: update inbox page & filter
        const response = await inboxService.getPersonalInbox(
          personalInboxId,
          pageNo,
          filter,
          pageSize,
        );
        const { error, page, count } = response.data;

        if (error) {
          throw error;
        }

        // Get AirBnB Data if there is any
        const airBnBIds = getAirbnbThreadIds(
          (page || []).flatMap((thread) => thread.sources || []),
        );

        try {
          if (airBnBIds.length !== 0) {
            let airbnbThreadsResp = await getAirBnBThreads(airBnBIds);
            if (airbnbThreadsResp?.data?.error) {
              console.log(
                "Error getting AirBnB threads",
                airbnbThreadsResp?.data?.error,
              );
            }

            (page || []).forEach((t) => {
              let airbnbThreads = airbnbThreadsResp?.data?.threads || [];
              t.sources = attachAirbnbThreadsToSources(
                t.sources,
                airbnbThreads,
              );
            });
          }
        } catch (e) {
          console.warn("Couldn't get AirBnB data");
        }

        commitInboxPage({
          ...request,
          pageNo,
          page,
          count,
          background: stopLoading,
        });
        return response.data;
      } catch (err) {
        console.warn(err);
        throw err;
      } finally {
        if (!stopLoading) setLoading(false);
      }
    },

    selectThread: async (threadId) => {
      const requestId = beginThreadSelection(threadId);
      setLoading(true);
      try {
        // Fetch the first page of messages
        const response = await inboxService.getInboxThread(threadId);
        const { error, thread } = response.data;
        confirmThreadRead(threadId);
        if (!isCurrentThreadRequest(threadId, requestId)) return thread;
        setThreadDetail((previous) =>
          mergeThreadDetailSnapshot(previous, thread),
        );
        const initialOffset = thread.messages ? thread.messages.length : 0;
        setMessageOffset(initialOffset);
        messageOffsetRef.current = initialOffset;
        return thread;
      } catch (err) {
        console.log("error:", err);
      } finally {
        if (isCurrentThreadRequest(threadId, requestId)) setLoading(false);
      }
    },
    selectPaginatedThread: async (threadId, index) => {
      const requestId = beginThreadSelection(threadId);
      setLoading(true);
      try {
        // Fetch the first page of messages
        const response = await inboxService.getPaginatedInboxThread(threadId, {
          offset: 0,
          limit: 30,
        });
        const { error, thread } = response.data;
        confirmThreadRead(threadId);

        // Get AirBnB Data if it's an AirBnB thread
        try {
          let source = (thread?.sources || []).find(isAirbnbSource);
          let airBnBId = source?.ota_id;

          console.log(airBnBId);

          if (!!airBnBId) {
            let airbnbThreadsResp = await getAirBnBThreads([airBnBId]);
            console.log(airbnbThreadsResp);
            if (airbnbThreadsResp?.data?.error) {
              console.log(
                "Error getting AirBnB threads",
                airbnbThreadsResp?.data?.error,
              );
            }

            console.log(airbnbThreadsResp?.data);

            thread.sources = attachAirbnbThreadsToSources(
              thread.sources,
              airbnbThreadsResp?.data?.threads || [],
            );
          }
        } catch (e) {
          console.log("Error getting AirBnB thread", e);
        }

        console.log("Thread Detail:", thread);

        if (!isCurrentThreadRequest(threadId, requestId)) return thread;
        setThreadDetail((previous) =>
          mergeThreadDetailSnapshot(previous, thread),
        );
        const initialOffset = thread.messages ? thread.messages.length : 0;
        setMessageOffset(initialOffset);
        messageOffsetRef.current = initialOffset;
        return thread;
      } catch (err) {
        console.log("error:", err);
      } finally {
        if (isCurrentThreadRequest(threadId, requestId)) setLoading(false);
      }
    },
    selectPersonalPaginatedThread: async (threadId, personalInboxId, index) => {
      if (legacy) {
        return contextInbox.selectPaginatedThread(threadId, index);
      }

      const requestId = beginThreadSelection(threadId);
      setLoading(true);
      try {
        // Fetch the first page of messages
        const response = await inboxService.getPersonalPaginatedInboxThread(
          threadId,
          personalInboxId,
          {
            offset: 0,
            limit: 30,
          },
        );
        const { error, thread } = response.data;
        confirmThreadRead(threadId);

        // Get AirBnB Data if it's an AirBnB thread
        try {
          let source = (thread?.sources || []).find(isAirbnbSource);
          let airBnBId = source?.ota_id;

          console.log(airBnBId);

          if (!!airBnBId) {
            let airbnbThreadsResp = await getAirBnBThreads([airBnBId]);
            console.log(airbnbThreadsResp);
            if (airbnbThreadsResp?.data?.error) {
              console.log(
                "Error getting AirBnB threads",
                airbnbThreadsResp?.data?.error,
              );
            }

            console.log(airbnbThreadsResp?.data);

            thread.sources = attachAirbnbThreadsToSources(
              thread.sources,
              airbnbThreadsResp?.data?.threads || [],
            );
          }
        } catch (e) {
          console.log("Error getting AirBnB thread", e);
        }

        console.log("Thread Detail:", thread);

        if (!isCurrentThreadRequest(threadId, requestId)) return thread;
        setThreadDetail((previous) =>
          mergeThreadDetailSnapshot(previous, thread),
        );
        const initialOffset = thread.messages ? thread.messages.length : 0;
        setMessageOffset(initialOffset);
        messageOffsetRef.current = initialOffset;
        return thread;
      } catch (err) {
        console.log("error:", err);
      } finally {
        if (isCurrentThreadRequest(threadId, requestId)) setLoading(false);
      }
    },
    loadMoreMessages: async (threadId, offset, limit = 30) => {
      // Fetch older messages for infinite scroll
      try {
        const response = await inboxService.getPaginatedInboxThread(threadId, {
          offset,
          limit,
        });
        const { error, thread } = response.data;
        if (error) return 0;
        if (!sameThreadId(selectedThreadIdRef.current, threadId)) return 0;

        const apiCount = (thread.messages || []).length;
        updateCurrentThreadDetail(threadId, (prev) => {
          if (!prev || !prev.messages) return prev;
          // Remove duplicates (in case of overlap)
          const existingIds = new Set(prev.messages.map((m) => m.message_id));
          const newMessages = (thread.messages || []).filter(
            (m) => !existingIds.has(m.message_id),
          );
          return {
            ...prev,
            messages: [...newMessages, ...prev.messages],
          };
        });
        setMessageOffset((prev) => {
          const newOffset = prev + apiCount;
          messageOffsetRef.current = newOffset;
          return newOffset;
        });
        return apiCount;
      } catch (err) {
        console.log("error loading more messages:", err);
        return 0;
      }
    },
    loadMorePersonalMessages: async (
      threadId,
      personalInboxId,
      offset,
      limit = 30,
    ) => {
      if (legacy) {
        return contextInbox.loadMoreMessages(threadId, offset, limit);
      }

      // Fetch older messages for infinite scroll
      try {
        const response = await inboxService.getPersonalPaginatedInboxThread(
          threadId,
          personalInboxId,
          {
            offset,
            limit,
          },
        );
        const { error, thread } = response.data;
        if (error) return 0;
        if (!sameThreadId(selectedThreadIdRef.current, threadId)) return 0;

        const apiCount = (thread.messages || []).length;
        updateCurrentThreadDetail(threadId, (prev) => {
          if (!prev || !prev.messages) return prev;
          // Remove duplicates (in case of overlap)
          const existingIds = new Set(prev.messages.map((m) => m.message_id));
          const newMessages = (thread.messages || []).filter(
            (m) => !existingIds.has(m.message_id),
          );
          return {
            ...prev,
            messages: [...newMessages, ...prev.messages],
          };
        });
        setMessageOffset((prev) => {
          const newOffset = prev + apiCount;
          messageOffsetRef.current = newOffset;
          return newOffset;
        });
        return apiCount;
      } catch (err) {
        console.log("error loading more messages:", err);
        return 0;
      }
    },
    getThreadDetail: async (thread_id) => {
      try {
        const response = await inboxService.getInboxThread(thread_id);
        const { error, thread } = response.data;
        confirmThreadRead(thread_id);
        updateCurrentThreadDetail(thread_id, (previous) =>
          mergeThreadDetailSnapshot(previous, thread),
        );
        return thread;
      } catch (err) {
        console.warn(err);
      }
    },
    getPaginatedThreadDetail: async (
      thread_id,
      offset = 0,
      limit = 30,
      isActive,
    ) => {
      const requestId = detailRequestRef.current;
      const canApply = () =>
        (!isActive || isActive()) && isCurrentThreadRequest(thread_id, requestId);
      try {
        const response = await inboxService.getPaginatedInboxThread(thread_id, {
          offset,
          limit,
        });
        const { error, thread } = response.data;
        if (error) return 0;
        if (!sameThreadId(thread?.id ?? thread?.thread_id, thread_id)) return;
        // Other screens also use this method to read a conversation. Return
        // its data, but never apply a cancelled response to the open thread.
        if (!canApply()) return isActive ? undefined : thread;
        confirmThreadRead(thread_id);

        updateCurrentThreadDetail(thread_id, (previous) =>
          canApply() ? mergeThreadDetailSnapshot(previous, thread) : previous,
        );

        return thread;
      } catch (err) {
        console.warn(err);
      }
    },
    getPersonalPaginatedThreadDetail: async (
      thread_id,
      personal_inbox_id,
      offset = 0,
      limit = 30,
      isActive,
    ) => {
      if (legacy) {
        return contextInbox.getPaginatedThreadDetail(
          thread_id, offset, limit, isActive,
        );
      }

      const requestId = detailRequestRef.current;
      const canApply = () =>
        (!isActive || isActive()) && isCurrentThreadRequest(thread_id, requestId);
      try {
        const response = await inboxService.getPersonalPaginatedInboxThread(
          thread_id,
          personal_inbox_id,
          {
            offset,
            limit,
          },
        );
        const { error, thread } = response.data;
        if (error) return 0;
        if (!sameThreadId(thread?.id ?? thread?.thread_id, thread_id)) return;
        if (!canApply()) return isActive ? undefined : thread;
        confirmThreadRead(thread_id);

        updateCurrentThreadDetail(thread_id, (previous) =>
          canApply() ? mergeThreadDetailSnapshot(previous, thread) : previous,
        );

        return thread;
      } catch (err) {
        console.warn(err);
      }
    },
    setMeta: async (thread_id, data) => {
      return mutateMeta(thread_id, data.token, data.state, () =>
        inboxService.setMeta(thread_id, data),
      );
    },
    setPersonalMeta: async (thread_id, personal_inbox_id, token, set) => {
      if (legacy) {
        return contextInbox.setMeta(thread_id, {
          thread_id: thread_id,
          token: token,
          state: set,
        });
      }

      return mutateMeta(thread_id, token, set, () =>
        inboxService.setPersonalMeta(
          thread_id,
          personal_inbox_id,
          token,
          set,
        ),
      );
    },
    togglePersonalMeta: async (thread_id, personal_inbox_id, token) => {
      if (legacy) {
        return contextInbox.setMeta(thread_id, {
          thread_id: thread_id,
          token: token,
        });
      }

      return mutateMeta(thread_id, token, undefined, () =>
        inboxService.togglePersonalMeta(
          thread_id,
          personal_inbox_id,
          token,
        ),
      );
    },
    toggleInboxRestriction: async (personal_inbox_id, token) => {
      let result = await inboxService.toggleInboxRestriction(
        personal_inbox_id,
        token,
      );

      return result.data;
    },

    sendMessage: async (objMessage) => {
      setIsSending(true);
      try {
        const result = await MessagesService.sendMessage(objMessage);
        return result.data;
      } finally {
        setIsSending(false);
      }
    },
    appendOutgoingMessage: (threadId, message) => {
      setThreadDetailState((previous) =>
        appendMessageToCurrentThread(
          previous,
          selectedThreadIdRef.current,
          threadId,
          message,
        ),
      );
    },
    promoteInboxThread: (threadId, message) => {
      threadMutationRef.current[threadId] = ++mutationSequenceRef.current;
      setInbox((previous) =>
        promoteInboxThreadInList(previous, threadId, message),
      );
    },
    upsertInboxThread: (thread) => {
      if (!thread?.id) return;

      const exists = (Array.isArray(inboxRef.current)
        ? inboxRef.current
        : []
      ).some((current) => sameThreadId(current.id, thread.id));
      threadMutationRef.current[thread.id] = ++mutationSequenceRef.current;
      setInbox((previous) =>
        mergeInboxPage(previous, [thread], { position: "front" }),
      );
      if (!exists) {
        setThreadCnt((count) => Number(count || 0) + 1);
      }
    },
    receiveRealtimeMessage: (threadId, message, viewing = false) => {
      threadMutationRef.current[threadId] = ++mutationSequenceRef.current;
      const result = applyRealtimeInboxMessage(
        inboxRef.current,
        threadId,
        message,
        {
          viewing,
          filter: activeQueryRef.current.filter,
        },
      );
      inboxRef.current = result.threads;
      setInboxState(result.threads);
      if (result.removed) {
        setThreadCnt((count) => Math.max(0, Number(count) - 1));
      }
      if (
        message?.client_message_id &&
        message.message_id !== null &&
        message.message_id !== undefined
      ) {
        setThreadDetailState((previous) =>
          confirmPendingMessageInCurrentThread(
            previous,
            selectedThreadIdRef.current,
            threadId,
            message,
          ),
        );
      }
      return result.found;
    },
    removeOutgoingMessage: (threadId, clientMessageId) => {
      setThreadDetailState((previous) =>
        removeMessageFromCurrentThread(
          previous,
          selectedThreadIdRef.current,
          threadId,
          clientMessageId,
        ),
      );
    },
    sendInternalNote: async (objMessage) => {
      const result = await MessagesService.sendInternalNote(objMessage);
      return result.data;
    },
    updateInternalNote: async (messageId, objMessage) => {
      const result = await MessagesService.updateInternalNote(
        messageId,
        objMessage,
      );
      return result.data;
    },
    deleteInternalNote: async (messageId) => {
      const result = await MessagesService.deleteInternalNote(messageId);
      return result.data;
    },
  };

  contextInbox.messageOffsetRef = messageOffsetRef;

  return (
    <InboxContext.Provider value={contextInbox}>
      {children}
    </InboxContext.Provider>
  );
};
